/**
 * ACA premium tax credit constants for the 2026 coverage year (spec §11).
 *
 * VERIFIED 2026-08-03: the enhanced (ARPA/IRA) subsidies expired 2026-01-01
 * and no extension has become law (House passed one 2026-01-08; stalled in the
 * Senate). The ORIGINAL structure is in force for 2026: credits only from 100%
 * to 400% of FPL, with a HARD CLIFF at 400% — one extra dollar of MAGI can
 * cost thousands in credit. Sources: IRS Rev. Proc. 2025-25 (percentages);
 * 2025 HHS poverty guidelines (FPL); CRS R48290 / KFF (cliff status).
 * If Congress later enacts a retroactive extension, flip CLIFF_IN_FORCE and
 * update the bands.
 */
export const ACA_CONSTANTS_AS_OF = '2026-08-03';

/** 2025 HHS poverty guidelines (contiguous US) — used for 2026 coverage. */
export const FPL_BASE_2026 = 15_650; // 1-person household
export const FPL_PER_ADDITIONAL_2026 = 5_500;

/** `scale` inflates the guidelines for future years — HHS reissues them every
 *  January from CPI, so a flat FPL against growing nominal MAGI would push
 *  everyone over the 400% cliff eventually. */
export function fplFor(householdSize: number, scale = 1): number {
  return (FPL_BASE_2026 + Math.max(0, householdSize - 1) * FPL_PER_ADDITIONAL_2026) * scale;
}

/** Whether the 400%-of-FPL hard subsidy cliff is in force. */
export const CLIFF_IN_FORCE = true;

/**
 * Applicable percentage of MAGI the household must contribute toward the
 * benchmark plan, by FPL ratio band, linearly interpolated within bands
 * (2026 pre-ARPA structure, indexed).
 */
const BANDS: { upTo: number; startPct: number; endPct: number }[] = [
  { upTo: 1.33, startPct: 0.021, endPct: 0.021 },
  { upTo: 1.5, startPct: 0.0314, endPct: 0.0419 },
  { upTo: 2.0, startPct: 0.0419, endPct: 0.066 },
  { upTo: 2.5, startPct: 0.066, endPct: 0.0844 },
  { upTo: 3.0, startPct: 0.0844, endPct: 0.0996 },
  { upTo: 4.0, startPct: 0.0996, endPct: 0.0996 },
];

/**
 * Applicable percentage for a MAGI-to-FPL ratio, or null when no credit is
 * available (below 100% FPL — outside the marketplace credit — or above 400%
 * with the cliff in force).
 */
export function applicablePercentage(fplRatio: number): number | null {
  if (fplRatio < 1.0) return null;
  if (CLIFF_IN_FORCE && fplRatio > 4.0) return null;
  let lower = 1.0;
  for (const band of BANDS) {
    if (fplRatio <= band.upTo) {
      const t = band.upTo === lower ? 0 : (fplRatio - lower) / (band.upTo - lower);
      return band.startPct + t * (band.endPct - band.startPct);
    }
    lower = band.upTo;
  }
  return BANDS[BANDS.length - 1]!.endPct;
}

/** Default benchmark silver premium, $/month per person near retirement age. */
export const DEFAULT_BENCHMARK_MONTHLY_60PLUS = 1_100;
